import { reporter } from '~/hooks/useErrorReporter';

export const safeGtag = (...args: Array<any>): void => {
  try {
    const gtag = window.gtag;
    if (gtag) {
      (gtag as (...args: Array<any>) => void)(...args);
    }
  } catch {
    // Do nothing
  }
};

export const safeClarity = (...args: Array<any>): void => {
  try {
    const clarity = window.clarity;
    if (typeof clarity === 'function') {
      (clarity as (...args: Array<any>) => void)(...args);
    }
  } catch {
    // Do nothing
  }
};

type TrackEventArgs = {
  eventName: string;
  parameters?: Record<string, any>;
};

export const trackEvent = ({ eventName, parameters }: TrackEventArgs) => {
  const trackingId = process.env.GA_ID;

  try {
    safeGtag('event', eventName, {
      ...parameters,
      send_to: trackingId,
    });
  } catch (error: any) {
    reporter.captureException(
      new Error(`Failed to track event: ${eventName} - ${error.message}`),
      'fatal',
    );
  }
};

type InitializeClarityArgs = {
  userId: string;
  accountId: string;
};
/**
 * Initializes Microsoft Clarity tracking.
 * Should only be called after user has given Analytics consent.
 */
export const initializeClarity = ({
  userId,
  accountId,
}: InitializeClarityArgs) => {
  if (window.__CLARITY_INITIALIZED__) return;

  const clarityId = process.env.CLARITY_ID;
  if (clarityId) {
    // Same logic as the original Clarity snippet, but TS/ESLint-friendly
    (function (
      c: Window & typeof globalThis,
      l: Document,
      a: 'clarity',
      r: 'script',
      i: string,
    ) {
      (c as any)[a] =
        (c as any)[a] ||
        function (...args: Array<any>) {
          ((c as any)[a].q = (c as any)[a].q || []).push(args);
        };

      const t = l.createElement(r); // HTMLScriptElement
      t.async = true;
      t.src = 'https://www.clarity.ms/tag/' + i;

      const firstScript = l.getElementsByTagName(r)[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(t, firstScript);
      } else {
        // fallback if for some reason there is no other <script>
        l.head?.appendChild(t);
      }
    })(window, document, 'clarity', 'script', clarityId);

    safeClarity('identify', userId);
    // Can be used for segments and filters in Clarity
    safeClarity('set', 'user_id', userId);
    safeClarity('set', 'account_id', accountId);

    window.__CLARITY_INITIALIZED__ = true;
  } else {
    reporter.captureException(
      new Error(`Failed to initialize Clarity - Clarity ID is not set`),
      'fatal',
    );
  }
};

type InitializeGAAnalyticsArgs = {
  userId: string;
};
/**
 * Updates gtag consent and reinitializes config when user interacts with the cookie banner
 * Only runs once to prevent duplicate events, even across hot module reloading
 */
export const initializeGAAnalytics = async ({
  userId,
}: InitializeGAAnalyticsArgs) => {
  if (window.__GA_INITIALIZED__) return;

  const gaId = process.env.GA_ID;

  if (!gaId) {
    reporter.captureException(
      new Error(`Failed to initialize GA Analytics - GA ID is not set`),
      'fatal',
    );
    return;
  }

  // Load GA script
  await new Promise<void>(resolve => {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    script.onload = () => resolve();
    document.head.appendChild(script);
  });

  window.gtag('js', new Date());

  // Apply config
  window.gtag('config', gaId, {
    cookie_flags: 'max-age=7200;secure;samesite=none',
    anonymize_ip: true,
    // We are in SPA, so we need to track pages manually.
    // GA page_view automatic event only tracks the initial page load.
    // Since we use React routing, path changes does not trigger full page reload,
    // so no automatic page_view events fire.
    // That's why `send_page_view` is set to false.
    send_page_view: false,
    user_id: userId,
  });

  window.__GA_INITIALIZED__ = true;
};
